import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useSelector, useDispatch } from 'react-redux'
import styled, { keyframes, css } from 'styled-components'
import useGetWindowSize from 'hooks/useGetWindowSize'
import HomeComponentSmall from './HomeComponentSmall'
import Loader from 'loaders/Loader'
import { pastravzta } from 'features/auth/authSlice'
import logo from '../../prayselyLogo.png'
import { HiArrowRight } from 'react-icons/hi2'
import { PiBookOpenTextLight } from 'react-icons/pi'
import { FaApple, FaGooglePlay } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'

const HERO_IMAGE = 'https://praysely.blob.core.windows.net/images/lp-prys.jpg'

function HomeComponent() {
  const { t } = useTranslation('web')
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const { isLoading, isError, message } = useSelector((state) => state.posts)
  const [screenWidth] = useGetWindowSize()
  const [scrolled, setScrolled] = useState(false)

  const handleLogoClick = () => {
    if (user) {
      if (user.isOrganization || user.isChurch) {
        navigate(`/${user.name}`)
      } else {
        navigate('/home')
      }
    } else {
      navigate(`/`)
    }
  }

  useEffect(() => {
    dispatch(pastravzta())
  }, [])

  useEffect(() => {
    if (isError) console.log(message)
    if (user) navigate('/home')
  }, [user, navigate, isError, message])

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20)
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  const handleLearnMore = () => {
    const element = document.getElementById('learn-more')
    if (element) element.scrollIntoView({ behavior: 'smooth' })
  }

  if (isLoading) return <Loader />
  if (screenWidth < 768) return <HomeComponentSmall />

  return (
    <Hero>
      <BackgroundImage style={{ backgroundImage: `url(${HERO_IMAGE})` }} />
      <GradientOverlay />
      <NoiseLayer />

      <Header $scrolled={scrolled}>
        <LogoWrap onClick={handleLogoClick}>
          <img src={logo} alt="Praysely" />
        </LogoWrap>
        <Nav>
          <Link to='/login'><GhostBtn>{t('landing.login')}</GhostBtn></Link>
          <Link to='/register'><PrimaryBtn>{t('landing.getStarted')}</PrimaryBtn></Link>
        </Nav>
      </Header>

      <HeroContent>
        <Eyebrow>{t('landing.eyebrow')}</Eyebrow>
        <Headline>
          {t('landing.church')}<br />
          {t('landing.community')}<br />
          <Accent>{t('landing.onePlace')}</Accent>
        </Headline>
        <Subhead>
          {t('landing.heroBody')}
        </Subhead>

        <CTARow>
          <CTAPrimary onClick={() => navigate('/register')}>
            <span>{t('landing.join')}</span>
            <FreeTag>{t('landing.freeForever')}</FreeTag>
            <HiArrowRight size={20} />
          </CTAPrimary>
          <CTAGhost onClick={handleLearnMore}>
            <PiBookOpenTextLight size={22} />
            <span>{t('landing.learnMore')}</span>
          </CTAGhost>
        </CTARow>

        <MobileAvailability>
          <AvailabilityText>{t('landing.availableOn')}</AvailabilityText>
          <StoreBadges>
            <StoreBadge>
              <FaApple size={22} />
              <span>iOS</span>
            </StoreBadge>
            <StoreBadge>
              <FaGooglePlay size={19} />
              <span>Android</span>
            </StoreBadge>
          </StoreBadges>
        </MobileAvailability>

        <ScrollHint onClick={handleLearnMore}>
          <span>{t('landing.scroll')}</span>
          <ScrollLine />
        </ScrollHint>
      </HeroContent>
    </Hero>
  )
}

export default HomeComponent

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(24px); }
  to   { opacity: 1; transform: translateY(0); }
`

const fadeIn = keyframes`
  from { opacity: 0; }
  to   { opacity: 1; }
`

const lineDrift = keyframes`
  0%   { transform: scaleY(0); transform-origin: top; }
  50%  { transform: scaleY(1); transform-origin: top; }
  51%  { transform: scaleY(1); transform-origin: bottom; }
  100% { transform: scaleY(0); transform-origin: bottom; }
`

const Hero = styled.section`
  position: relative;
  width: 100%;
  min-height: 100vh;
  overflow: hidden;
  background: #0f1108;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
`

const BackgroundImage = styled.div`
  position: absolute;
  inset: 0;
  background-size: cover;
  background-position: center;
  filter: saturate(1.05) contrast(1.02);
  animation: ${fadeIn} 1.2s ease both;
`

const GradientOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    radial-gradient(80% 60% at 20% 30%, rgba(111, 149, 46, 0.35) 0%, transparent 60%),
    radial-gradient(70% 50% at 90% 80%, rgba(20, 30, 8, 0.55) 0%, transparent 70%),
    linear-gradient(180deg, rgba(15, 17, 8, 0.25) 0%, rgba(15, 17, 8, 0.65) 100%);
`

const NoiseLayer = styled.div`
  position: absolute;
  inset: 0;
  opacity: 0.05;
  pointer-events: none;
  background-image: url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='160' height='160'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)' opacity='0.6'/></svg>");
`

const Header = styled.header`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 50;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 40px;
  transition: all 0.35s ease;

  ${(p) => p.$scrolled && css`
    background: rgba(15, 17, 8, 0.55);
    backdrop-filter: saturate(150%) blur(14px);
    -webkit-backdrop-filter: saturate(150%) blur(14px);
    border-bottom: 1px solid rgba(255, 255, 255, 0.08);
    padding: 12px 40px;
  `}
`

const LogoWrap = styled.div`
  cursor: pointer;
  display: flex;
  align-items: center;
  img {
    height: 38px;
    width: auto;
    display: block;
  }
`

const Nav = styled.nav`
  display: flex;
  align-items: center;
  gap: 10px;
`

const GhostBtn = styled.button`
  background: transparent;
  color: #f5f5f0;
  border: 1px solid rgba(255, 255, 255, 0.18);
  height: 42px;
  padding: 0 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.08);
    border-color: rgba(255, 255, 255, 0.3);
  }
`

const PrimaryBtn = styled.button`
  background: #f5f5f0;
  color: #2a3a10;
  border: none;
  height: 42px;
  padding: 0 20px;
  border-radius: 999px;
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background: #ffffff;
    transform: translateY(-1px);
  }
`

const HeroContent = styled.div`
  position: relative;
  z-index: 2;
  max-width: 1200px;
  margin: 0 auto;
  padding: 180px 40px 80px;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  text-align: left;
`

const Eyebrow = styled.div`
  font-size: 13px;
  font-weight: 500;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: #c9d4a8;
  padding: 8px 16px;
  border: 1px solid rgba(201, 212, 168, 0.3);
  border-radius: 999px;
  background: rgba(111, 149, 46, 0.12);
  backdrop-filter: blur(8px);
  animation: ${fadeUp} 0.8s 0.05s ease both;
`

const Headline = styled.h1`
  font-size: clamp(48px, 7vw, 96px);
  font-weight: 700;
  line-height: 1.02;
  letter-spacing: -0.035em;
  color: #ffffff;
  margin: 28px 0 0;
  animation: ${fadeUp} 0.9s 0.15s ease both;
`

const Accent = styled.span`
  background: linear-gradient(135deg, #b5d671 0%, #6f952e 60%, #4a6b1c 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  font-style: italic;
  font-weight: 600;
`

const Subhead = styled.p`
  font-size: clamp(18px, 1.4vw, 22px);
  font-weight: 400;
  line-height: 1.55;
  color: rgba(245, 245, 240, 0.78);
  max-width: 620px;
  margin: 28px 0 0;
  letter-spacing: -0.01em;
  animation: ${fadeUp} 0.9s 0.25s ease both;
`

const CTARow = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  margin-top: 44px;
  animation: ${fadeUp} 0.9s 0.35s ease both;
  flex-wrap: wrap;
`

const CTAPrimary = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  height: 60px;
  padding: 0 28px 0 32px;
  border: none;
  border-radius: 14px;
  background: linear-gradient(135deg, #8cb83a 0%, #6f952e 100%);
  color: #ffffff;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: -0.01em;
  cursor: pointer;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.15) inset,
    0 12px 28px rgba(111, 149, 46, 0.35),
    0 2px 6px rgba(0, 0, 0, 0.25);
  transition: all 0.25s ease;

  svg { transition: transform 0.25s ease; }

  &:hover {
    transform: translateY(-2px);
    box-shadow:
      0 1px 0 rgba(255, 255, 255, 0.2) inset,
      0 18px 36px rgba(111, 149, 46, 0.45),
      0 4px 10px rgba(0, 0, 0, 0.3);
    svg { transform: translateX(4px); }
  }
`

const FreeTag = styled.span`
  font-size: 11px;
  font-weight: 500;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.18);
  color: #ffffff;
  letter-spacing: 0.04em;
  text-transform: uppercase;
`

const CTAGhost = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  height: 60px;
  padding: 0 28px;
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.04);
  backdrop-filter: blur(10px);
  color: #f5f5f0;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.01em;
  cursor: pointer;
  transition: all 0.25s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.1);
    border-color: rgba(255, 255, 255, 0.35);
    transform: translateY(-2px);
  }
`

const MobileAvailability = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  margin-top: 30px;
  padding: 10px 12px 10px 18px;
  width: fit-content;
  border: 1px solid rgba(255, 255, 255, 0.16);
  border-radius: 18px;
  background: rgba(15, 17, 8, 0.28);
  backdrop-filter: blur(14px);
  -webkit-backdrop-filter: blur(14px);
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.08) inset,
    0 16px 34px rgba(0, 0, 0, 0.18);
  animation: ${fadeUp} 0.9s 0.43s ease both;
`

const AvailabilityText = styled.span`
  color: rgba(245, 245, 240, 0.74);
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  white-space: nowrap;
`

const StoreBadges = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`

const StoreBadge = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  width: 126px;
  height: 44px;
  border-radius: 13px;
  background: #f5f5f0;
  color: #1f2a12;
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0;
  box-shadow:
    0 1px 0 rgba(255, 255, 255, 0.7) inset,
    0 8px 18px rgba(0, 0, 0, 0.22);

  svg {
    flex: 0 0 auto;
  }
`

const ScrollHint = styled.div`
  margin-top: 96px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  align-self: center;
  cursor: pointer;
  color: rgba(245, 245, 240, 0.55);
  font-size: 12px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  animation: ${fadeUp} 1s 0.5s ease both;

  &:hover { color: rgba(245, 245, 240, 0.85); }
`

const ScrollLine = styled.div`
  width: 1px;
  height: 48px;
  background: linear-gradient(180deg, rgba(245, 245, 240, 0.5), transparent);
  animation: ${lineDrift} 2.4s ease-in-out infinite;
`
